import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

const CLOUDINARY_IMAGE_UPLOAD_MARKER = '/image/upload/';
const CLOUDINARY_IMAGE_LIST_BASE_URL = 'https://media.bkstr.com/image/list/';
const PILOT_ENHANCED_PUBLIC_ID_SUFFIXES = ['E01', 'E02', 'E03', 'E04'];
const ROLLOUT_ENHANCED_PUBLIC_ID_SUFFIXES = ['01', '02', '03', '04'];
const DEFAULT_ENHANCED_IMAGE_WIDTHS = [240, 352, 832, 1200, 1600, 1920, 2560, 3840];
const ENHANCED_ASSETS_TIMEOUT_MS = 2000;
const ENHANCED_ASSETS_MISS_STORAGE_KEY = 'theme:enhanced-pdp-assets-misses';
const ENHANCED_ASSETS_PROBE_MISS_STORAGE_KEY = 'theme:enhanced-pdp-assets-probe-misses';
const ENHANCED_ASSETS_MISS_TTL_MS = 24 * 60 * 60 * 1000;

/**
 * @param {string} event
 * @param {unknown} [details]
 */
function logClaidPdp(event, details) {
  if (typeof window.__claidLog === 'function') {
    window.__claidLog('PDP', event, details);
    return;
  }

  console.log('[CLAID][PDP]', event, details ?? '');
}

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    this.addEventListener('error', this.#handleEnhancedImageError, { capture: true, signal });

    this.#applyEnhancedPdpAssets(signal);
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    const currentMediaKey = this.dataset.galleryMediaKey;
    const newMediaKey = newMediaGallery.dataset.galleryMediaKey;

    if (currentMediaKey && newMediaKey && currentMediaKey === newMediaKey) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Checks Cloudinary for ClaidAI-enhanced PDP assets and swaps them in before the gallery is revealed.
   * If no enhanced assets exist, the original Cloudinary gallery remains intact.
   *
   * @param {AbortSignal} signal
   */
  async #applyEnhancedPdpAssets(signal) {
    if (this.dataset.enhancedPdpAssets !== 'true') {
      logClaidPdp('skip: list disabled, keeping original Cloudinary gallery');
      this.#revealEnhancedPdpAssets();
      return;
    }

    let enhancedAssetsApplied = false;

    try {
      const sourceImage = this.querySelector('img.product-media__image');
      const sourceUrl = sourceImage instanceof HTMLImageElement ? sourceImage.currentSrc || sourceImage.src : '';
      const sourcePublicId = getCloudinaryPublicId(sourceUrl);
      const tag = getCloudinaryEnhancedTag(sourcePublicId);

      if (!tag) {
        logClaidPdp('skip: no list tag from current image, keeping original Cloudinary gallery', {
          sourceUrl,
          sourcePublicId,
        });
        return;
      }

      if (
        /_E0[1-4](?:\.[a-z]+)?(?:$|[/?])/i.test(sourceUrl) ||
        /_0[1-4](?:\.[a-z]+)?(?:$|[/?])/i.test(sourceUrl) ||
        /\/d_[^/]+_0[1-4]\./i.test(sourceUrl)
      ) {
        logClaidPdp('liquid already served enhanced gallery, skip JS swap', { tag, sourceUrl });
        this.#bindEnhancedExtraImageErrors();
        this.#syncEnhancedPdpControls();
        enhancedAssetsApplied = true;
        return;
      }

      // 1120 DAM files are `_E01`–`_E04`. Every other store is `_01`–`_04`.
      const usePilot = this.dataset.claidPilotStore === 'true';
      const suffixes = usePilot ? PILOT_ENHANCED_PUBLIC_ID_SUFFIXES : ROLLOUT_ENHANCED_PUBLIC_ID_SUFFIXES;
      const publicIds = suffixes.map((suffix) => `${tag}_${suffix}`);
      logClaidPdp(`swap gallery ← ${usePilot ? '_E01-_E04' : '_01-_04'}`, { tag, publicIds, sourcePublicId, usePilot });
      this.#renderEnhancedPdpAssets(publicIds);
      enhancedAssetsApplied = true;
      this.#bindEnhancedExtraImageErrors();
    } catch (error) {
      logClaidPdp('fail-open: error, keeping original Cloudinary gallery', {
        error: error instanceof Error ? error.message : String(error),
      });
    } finally {
      if (!signal.aborted) {
        if (!enhancedAssetsApplied) this.#removeEnhancedPdpPlaceholders();
        this.#revealEnhancedPdpAssets();
      }
    }
  }

  #bindEnhancedExtraImageErrors() {
    this.querySelectorAll('img[data-enhanced-public-id]').forEach((image) => {
      if (!(image instanceof HTMLImageElement)) return;
      const publicId = image.getAttribute('data-enhanced-public-id') || '';
      if (!/_E?0[2-4]$/i.test(publicId)) return;

      image.addEventListener(
        'error',
        () => {
          const slide = image.closest(
            'slideshow-slide, li.product-media-container, button.slideshow-controls__thumbnail, button.dialog-thumbnails-list__thumbnail'
          );
          if (!slide) return;
          slide.setAttribute('hidden', '');
          slide.setAttribute('aria-hidden', 'true');
          slide.setAttribute('data-claid-missing', '');
          this.#syncEnhancedPdpControls();
        },
        { once: true }
      );
    });
  }

  #handleEnhancedImageError = (event) => {
    const image = event.target;
    if (!(image instanceof HTMLImageElement) || !image.closest('[data-claid-extra-slide]')) return;

    // Liquid's inline fallback hides the failed slide/thumbnail at the target phase.
    // Wait until that handler has run before reconciling the navigation controls.
    queueMicrotask(() => this.#syncEnhancedPdpControls());
  };

  #syncEnhancedPdpControls() {
    const slideshow = this.slideshow;
    const scroller = slideshow?.querySelector('slideshow-slides');
    if (!slideshow || !scroller) return;

    const slides = Array.from(scroller.children).filter((element) => element.matches('slideshow-slide'));
    const visibleSlides = slides.filter((slide) => !slide.hasAttribute('hidden'));
    const visibleSlideCount = visibleSlides.length;
    const hasSingleSlide = visibleSlideCount <= 1;

    slideshow.setAttribute('data-generic-media-size', String(visibleSlideCount));
    slideshow.classList.toggle('product-media-gallery__slideshow--single-media', hasSingleSlide);
    slideshow.classList.toggle('slideshow--single-media', hasSingleSlide);

    slideshow.querySelectorAll('.slideshow-controls__thumbnails').forEach((thumbnailList) => {
      const thumbnails = Array.from(
        thumbnailList.querySelectorAll(':scope > button.slideshow-controls__thumbnail')
      );

      thumbnails.forEach((thumbnail, index) => {
        const slide = slides[index];
        const isMissing = !slide || slide.hasAttribute('hidden') || thumbnail.hasAttribute('data-claid-missing');
        const visibleIndex = slide ? visibleSlides.indexOf(slide) : -1;

        thumbnail.toggleAttribute('hidden', isMissing);

        if (isMissing || visibleIndex < 0) {
          thumbnail.setAttribute('aria-hidden', 'true');
          thumbnail.removeAttribute('ref');
          return;
        }

        thumbnail.removeAttribute('aria-hidden');
        thumbnail.setAttribute('ref', 'thumbnails[]');
        thumbnail.setAttribute('on:click', `/select/${visibleIndex}`);
      });
    });

    slideshow.querySelectorAll('slideshow-controls').forEach((controls) => {
      const hasVisibleThumbnail = controls.querySelector('.slideshow-controls__thumbnail:not([hidden])') !== null;
      controls.toggleAttribute('hidden', hasSingleSlide && !hasVisibleThumbnail);
    });

    slideshow
      .querySelectorAll(
        'slideshow-arrows, .slideshow-controls__arrows, .slideshow-controls__dots, .slideshow-controls__counter, .slideshow-controls__thumbnails-dots-container'
      )
      .forEach((controls) => controls.toggleAttribute('hidden', hasSingleSlide));

    slideshow.querySelectorAll('.slideshow-controls__dots').forEach((dots) => {
      const slidesPerScreen = parseInt(dots.getAttribute('data-slides-per-screen') || '1', 10) || 1;
      const dotCount = Math.ceil(visibleSlideCount / slidesPerScreen);
      const items = syncElementCount(dots, 'li', dotCount);
      const labelTemplate = dots.getAttribute('data-slide-label-template') || 'Slide [index] of [length]';

      items.forEach((item, index) => {
        const button = item.querySelector('button');
        if (!button) return;

        const slideIndex = index * slidesPerScreen;
        button.classList.toggle('is-selected', index === 0);
        button.style.setProperty('animation-timeline', `--slide-${index + 1}`);
        button.setAttribute(
          'aria-label',
          labelTemplate.replace('[index]', String(index + 1)).replace('[length]', String(dotCount))
        );
        button.setAttribute('on:click', `/select/${slideIndex}`);
        button.textContent = String(index + 1);
      });
    });

    slideshow.querySelectorAll('.slideshow-controls__counter').forEach((counter) => {
      const current = counter.querySelector('[ref="current"]');
      if (current) current.textContent = '1';
      const slash = counter.querySelector('.slash');
      if (slash?.nextSibling) slash.nextSibling.textContent = String(visibleSlideCount);
    });

    slideshow.updatedCallback?.();
    slideshow.current = Math.min(slideshow.current, Math.max(visibleSlideCount - 1, 0));
  }

  #revealEnhancedPdpAssets() {
    this.style.removeProperty('visibility');
    this.classList.remove('media-gallery--enhanced-checking');
  }

  #removeEnhancedPdpPlaceholders() {
    this.querySelectorAll('.enhanced-pdp-assets__placeholder-controls, .enhanced-pdp-assets__placeholder-arrows').forEach(
      (element) => element.remove()
    );

    const slideshow = this.querySelector('slideshow-component');
    const slideCount = this.querySelectorAll('slideshow-slide').length;
    if (slideshow && slideCount <= 1) {
      slideshow.classList.add('product-media-gallery__slideshow--single-media', 'slideshow--single-media');
    }
  }

  /**
   * @param {string[]} publicIds
   */
  #renderEnhancedPdpAssets(publicIds) {
    const imageUrls = publicIds.map((publicId) => ({
      publicId,
      url: this.#buildEnhancedImageUrl(publicId),
    }));

    this.#syncCarouselSlides(imageUrls);
    this.#syncGridItems(imageUrls);
    this.#syncSlideshowThumbnails(imageUrls);
    this.#syncZoomDialog(imageUrls);
    this.#syncEnhancedPdpControls();
    this.refs.zoomDialogComponent?.updatedCallback?.();
    this.slideshow?.select?.(0, undefined, { animate: false });
  }

  /**
   * @param {string} publicId
   * @param {number} [width]
   */
  #buildEnhancedImageUrl(publicId, width) {
    const prefix = (this.dataset.cloudinaryImagePrefix || 'https://media.bkstr.com/image/upload').replace(/\/$/, '');
    const transformation = normalizeCloudinaryTransformation(this.dataset.cloudinaryImageTransformation || '/f_auto,q_auto');
    const widthTransformation = width ? `/c_limit,w_${width}` : '';
    const defaultAsset = cloudinaryDefaultAssetParam(publicId, this.dataset.claidPilotStore === 'true');

    return `${prefix}${transformation}${widthTransformation}${defaultAsset}/${publicId}`;
  }

  /**
   * @param {{ publicId: string; url: string }[]} imageUrls
   */
  #syncCarouselSlides(imageUrls) {
    const scroller = this.querySelector('slideshow-slides');
    if (!scroller) return;

    const slides = syncElementCount(scroller, 'slideshow-slide', imageUrls.length);
    slides.forEach((slide, index) => {
      const imageUrl = imageUrls[index];
      if (!imageUrl) return;

      slide.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
      slide.style.setProperty('view-timeline-name', `--slide-${index}`);
      updateIndexedAction(slide, 'on:click', index);
      replaceImages(slide, imageUrl.publicId, (width) => this.#buildEnhancedImageUrl(imageUrl.publicId, width));
    });
  }

  /**
   * @param {{ publicId: string; url: string }[]} imageUrls
   */
  #syncGridItems(imageUrls) {
    const grid = this.querySelector('.media-gallery__grid');
    if (!grid) return;

    const items = syncElementCount(grid, 'li.product-media-container', imageUrls.length);
    items.forEach((item, index) => {
      const imageUrl = imageUrls[index];
      if (!imageUrl) return;

      item.setAttribute('ref', 'media[]');
      replaceImages(item, imageUrl.publicId, (width) => this.#buildEnhancedImageUrl(imageUrl.publicId, width));
    });
  }

  /**
   * @param {{ publicId: string; url: string }[]} imageUrls
   */
  #syncSlideshowThumbnails(imageUrls) {
    const thumbnailLists = this.querySelectorAll('.slideshow-controls__thumbnails');

    thumbnailLists.forEach((thumbnailList) => {
      const thumbnails = syncElementCount(thumbnailList, 'button.slideshow-controls__thumbnail', imageUrls.length);
      thumbnails.forEach((thumbnail, index) => {
        const imageUrl = imageUrls[index];
        if (!imageUrl) return;

        thumbnail.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
        thumbnail.setAttribute('on:click', `/select/${index}`);
        replaceImages(thumbnail, imageUrl.publicId, (width) => this.#buildEnhancedImageUrl(imageUrl.publicId, width || 144));
      });
    });

    this.querySelectorAll('.slideshow-controls__counter').forEach((counter) => {
      const current = counter.querySelector('[ref="current"]');
      if (current) current.textContent = '1';
      const slash = counter.querySelector('.slash');
      if (slash?.nextSibling) slash.nextSibling.textContent = String(imageUrls.length);
    });

    this.querySelectorAll('.slideshow-controls__dots').forEach((dots) => {
      const items = syncElementCount(dots, 'li', imageUrls.length);
      items.forEach((item, index) => {
        const button = item.querySelector('button');
        if (!button) return;

        button.classList.toggle('is-selected', index === 0);
        button.setAttribute('aria-label', `Slide ${index + 1} of ${imageUrls.length}`);
        button.setAttribute('on:click', `/select/${index}`);
        button.textContent = String(index + 1);
      });
    });
  }

  /**
   * @param {{ publicId: string; url: string }[]} imageUrls
   */
  #syncZoomDialog(imageUrls) {
    const thumbnailList = this.querySelector('.dialog-thumbnails-list');
    const zoomGallery = this.querySelector('.dialog-zoomed-gallery');

    if (thumbnailList) {
      const thumbnails = syncElementCount(thumbnailList, 'button.dialog-thumbnails-list__thumbnail', imageUrls.length);
      thumbnails.forEach((thumbnail, index) => {
        const imageUrl = imageUrls[index];
        if (!imageUrl) return;

        thumbnail.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
        thumbnail.setAttribute('on:click', `/handleThumbnailClick/${index}`);
        thumbnail.setAttribute('on:pointerenter', `/handleThumbnailPointerEnter/${index}`);
        replaceImages(thumbnail, imageUrl.publicId, (width) => this.#buildEnhancedImageUrl(imageUrl.publicId, width || 1024));
      });
    }

    if (zoomGallery) {
      const items = syncElementCount(zoomGallery, 'li.product-media-container', imageUrls.length);
      items.forEach((item, index) => {
        const imageUrl = imageUrls[index];
        if (!imageUrl) return;

        item.id = `product-enhanced-${imageUrl.publicId}-${index + 1}`;
        item.setAttribute('ref', 'media[]');
        replaceImages(item, imageUrl.publicId, (width) => this.#buildEnhancedImageUrl(imageUrl.publicId, width));
      });
    }
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Zooms the media gallery at the current slideshow index.
   * Used by the zoom-in control button.
   *
   * @param {PointerEvent} event - The pointer event.
   */
  zoomCurrent(event) {
    const index = this.refs.slideshow?.current ?? 0;
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

/**
 * @param {string} url
 */
function getCloudinaryPublicId(url) {
  if (!url) return '';

  try {
    const parsedUrl = new URL(url, window.location.origin);
    const path = decodeURIComponent(parsedUrl.pathname);
    const uploadIndex = path.indexOf(CLOUDINARY_IMAGE_UPLOAD_MARKER);
    const relevantPath = uploadIndex >= 0 ? path.slice(uploadIndex + CLOUDINARY_IMAGE_UPLOAD_MARKER.length) : path;
    const segments = relevantPath.split('/').filter(Boolean);
    const filename = segments.at(-1) || '';

    return stripFileExtension(filename);
  } catch (_error) {
    return '';
  }
}

/**
 * @param {string} publicId
 */
function getCloudinaryEnhancedTag(publicId) {
  if (!publicId) return '';

  const publicIdWithoutShopifyHash = publicId.replace(/_[a-z0-9]{6,}$/i, '');

  return publicIdWithoutShopifyHash.replace(/_E\d+$/i, '').replace(/_\d{2,}$/i, '');
}

/**
 * 1120 requests `_E0N` with `/d_{base}_0N.jpg`. Other stores request `_0N`
 * with no default so missing extras 404 and hide.
 *
 * @param {string} publicId
 * @param {boolean} usePilot
 */
function cloudinaryDefaultAssetParam(publicId, usePilot) {
  if (!usePilot) return '';

  const enhanced = publicId.match(/^(.*)_E(0[1-4])$/i);
  if (enhanced?.[1] && enhanced[2]) return `/d_${enhanced[1]}_${enhanced[2]}.jpg`;

  return '/d_gmdef.jpg';
}

/**
 * Most products have no ClaidAI-enhanced assets, and the gallery stays hidden until the
 * Cloudinary list request resolves. Remembering list 404s skips the JSON call on later
 * views; untagged `_E01` / `_01` files are still probed. Probe misses are stored separately
 * so a prior list 404 cannot hide enhanced images that PLP already shows via `d_{base}_01`.
 *
 * @param {string} storageKey
 * @returns {Record<string, number>} Expiry timestamps keyed by Cloudinary tag.
 */
function readEnhancedAssetsMisses(storageKey) {
  try {
    const raw = localStorage.getItem(storageKey);
    const stored = raw ? JSON.parse(raw) : null;

    return stored && typeof stored === 'object' ? stored : {};
  } catch {
    return {};
  }
}

/**
 * @param {string} storageKey
 * @param {string} tag
 */
function hasRecentEnhancedAssetsMiss(storageKey, tag) {
  const expiresAt = readEnhancedAssetsMisses(storageKey)[tag];

  return typeof expiresAt === 'number' && expiresAt > Date.now();
}

/**
 * @param {string} storageKey
 * @param {string} tag
 */
function rememberEnhancedAssetsMiss(storageKey, tag) {
  try {
    const now = Date.now();
    const misses = readEnhancedAssetsMisses(storageKey);

    for (const [key, expiresAt] of Object.entries(misses)) {
      if (typeof expiresAt !== 'number' || expiresAt <= now) delete misses[key];
    }

    misses[tag] = now + ENHANCED_ASSETS_MISS_TTL_MS;
    localStorage.setItem(storageKey, JSON.stringify(misses));
  } catch {
    // Private browsing or a full quota just means we probe again next time.
  }
}

/**
 * Resolve `_E01`/`_01`–`_04` without waiting on the list-by-tag 404.
 * First visits were burning the 2s budget on `image/list/{tag}.json`, then
 * fail-opening the original gallery; a reload skipped the list via miss cache
 * and the probe succeeded. Start probe immediately (PLP path) and only wait
 * for list when probe finds nothing.
 *
 * @param {string} tag
 * @param {AbortSignal} signal
 * @param {(publicId: string) => string} buildUrl
 */
async function resolveEnhancedPublicIds(tag, signal, buildUrl) {
  const skipProbe = hasRecentEnhancedAssetsMiss(ENHANCED_ASSETS_PROBE_MISS_STORAGE_KEY, tag);
  const listPromise = fetchEnhancedPublicIdsFromList(tag, signal).catch((error) => {
    if (signal.aborted) throw error;
    logClaidPdp('list failed, continuing with probe', {
      tag,
      error: error instanceof Error ? error.message : String(error),
    });
    return [];
  });

  if (skipProbe) {
    logClaidPdp('skip probe: 24h miss cache', { tag });
    return listPromise;
  }

  const fromProbe = await probeEnhancedPublicIds(tag, signal, buildUrl);
  if (signal.aborted) return [];

  if (fromProbe.length) {
    logClaidPdp('probe found untagged enhanced set', { tag, publicIds: fromProbe });
    listPromise.catch(() => {});
    return fromProbe;
  }

  const fromList = await listPromise;
  if (fromList.length) return fromList;

  rememberEnhancedAssetsMiss(ENHANCED_ASSETS_PROBE_MISS_STORAGE_KEY, tag);
  return [];
}

/**
 * @param {string} tag
 * @param {AbortSignal} signal
 */
async function fetchEnhancedPublicIdsFromList(tag, signal) {
  if (hasRecentEnhancedAssetsMiss(ENHANCED_ASSETS_MISS_STORAGE_KEY, tag)) {
    logClaidPdp('skip list: 24h miss cache', { tag });
    return [];
  }

  const listUrl = `${CLOUDINARY_IMAGE_LIST_BASE_URL}${encodeURIComponent(tag)}.json`;
  logClaidPdp('list request', { tag, listUrl });
  const response = await fetch(listUrl, { signal });

  if (response.status === 404) {
    logClaidPdp('list 404 — no tagged assets, probing _E01/_01', { tag, listUrl });
    rememberEnhancedAssetsMiss(ENHANCED_ASSETS_MISS_STORAGE_KEY, tag);
    return [];
  }
  if (!response.ok) throw new Error(`Cloudinary list request failed: ${response.status}`);

  const data = await response.json();
  const resources = Array.isArray(data)
    ? data
    : Array.isArray(data.resources)
      ? data.resources
      : Array.isArray(data.public_ids)
        ? data.public_ids
        : [];
  const pilotPublicIds = collectEnhancedPublicIdsBySuffix(resources, tag, PILOT_ENHANCED_PUBLIC_ID_SUFFIXES);

  if (pilotPublicIds.has('E01')) {
    return PILOT_ENHANCED_PUBLIC_ID_SUFFIXES.map((suffix) => pilotPublicIds.get(suffix)).filter(Boolean);
  }

  const rolloutPublicIds = collectEnhancedPublicIdsBySuffix(resources, tag, ROLLOUT_ENHANCED_PUBLIC_ID_SUFFIXES);

  if (rolloutPublicIds.has('01')) {
    return ROLLOUT_ENHANCED_PUBLIC_ID_SUFFIXES.map((suffix) => rolloutPublicIds.get(suffix)).filter(Boolean);
  }

  logClaidPdp('list 200 but no _E01 or _01 hero, probing filenames', { tag });
  rememberEnhancedAssetsMiss(ENHANCED_ASSETS_MISS_STORAGE_KEY, tag);
  return [];
}

/**
 * PLP does not use list-by-tag: it requests `{base}_E01` with Cloudinary `d_{base}_01`.
 * New-spec uploads often have `_01`–`_04` files without the list tag, so PDP must probe
 * the same public IDs or the gallery stays on the original Cloudinary asset.
 *
 * @param {string} tag
 * @param {AbortSignal} signal
 * @param {(publicId: string) => string} buildUrl
 */
async function probeEnhancedPublicIds(tag, signal, buildUrl) {
  const pilotHero = `${tag}_E01`;
  const rolloutHero = `${tag}_01`;
  const [pilotExists, rolloutExists] = await Promise.all([
    cloudinaryImageExists(buildUrl(pilotHero), signal),
    cloudinaryImageExists(buildUrl(rolloutHero), signal),
  ]);

  if (signal.aborted) return [];

  if (pilotExists) {
    const extras = await collectExistingPublicIds(tag, PILOT_ENHANCED_PUBLIC_ID_SUFFIXES.slice(1), buildUrl, signal);
    return [pilotHero, ...extras];
  }

  if (rolloutExists) {
    const extras = await collectExistingPublicIds(tag, ROLLOUT_ENHANCED_PUBLIC_ID_SUFFIXES.slice(1), buildUrl, signal);
    return [rolloutHero, ...extras];
  }

  return [];
}

/**
 * @param {string} tag
 * @param {string[]} suffixes
 * @param {(publicId: string) => string} buildUrl
 * @param {AbortSignal} signal
 */
async function collectExistingPublicIds(tag, suffixes, buildUrl, signal) {
  const publicIds = suffixes.map((suffix) => `${tag}_${suffix}`);
  const exists = await Promise.all(publicIds.map((publicId) => cloudinaryImageExists(buildUrl(publicId), signal)));

  return publicIds.filter((_, index) => exists[index]);
}

/**
 * @param {string} url
 * @param {AbortSignal} signal
 */
function cloudinaryImageExists(url, signal) {
  return new Promise((resolve) => {
    if (signal.aborted) {
      resolve(false);
      return;
    }

    const image = new Image();
    const finish = (exists) => {
      signal.removeEventListener('abort', onAbort);
      image.onload = null;
      image.onerror = null;
      resolve(exists);
    };
    const onAbort = () => finish(false);

    signal.addEventListener('abort', onAbort, { once: true });
    image.onload = () => finish(image.naturalWidth > 2);
    image.onerror = () => finish(false);
    image.src = url;
  });
}

/**
 * @param {unknown[]} resources
 * @param {string} tag
 * @param {string[]} suffixes
 * @returns {Map<string, string>}
 */
function collectEnhancedPublicIdsBySuffix(resources, tag, suffixes) {
  const publicIdsBySuffix = new Map();

  for (const resource of resources) {
    const publicId = typeof resource === 'string' ? resource : resource?.public_id;
    if (typeof publicId !== 'string') continue;

    const normalizedPublicId = stripFileExtension(publicId);
    const suffix = suffixes.find((expectedSuffix) => normalizedPublicId.endsWith(`${tag}_${expectedSuffix}`));

    if (suffix) publicIdsBySuffix.set(suffix, normalizedPublicId);
  }

  return publicIdsBySuffix;
}

/**
 * @param {Element} parent
 * @param {string} childSelector
 * @param {number} count
 */
function syncElementCount(parent, childSelector, count) {
  let elements = Array.from(parent.querySelectorAll(`:scope > ${childSelector}`));
  const template = elements[0];

  if (!template) return [];

  while (elements.length < count) {
    const clone = /** @type {Element} */ (template.cloneNode(true));
    parent.appendChild(clone);
    elements.push(clone);
  }

  while (elements.length > count) {
    elements.pop()?.remove();
  }

  return Array.from(parent.querySelectorAll(`:scope > ${childSelector}`));
}

/**
 * @param {Element} root
 * @param {string} publicId
 * @param {(width?: number) => string} buildUrl
 */
function replaceImages(root, publicId, buildUrl) {
  root.querySelectorAll('img').forEach((image) => {
    if (!(image instanceof HTMLImageElement)) return;

    const widths = getImageWidths(image);
    const primaryWidth = widths.at(-1);
    const srcset = widths.map((width) => `${buildUrl(width)} ${width}w`).join(', ');

    image.src = buildUrl(primaryWidth);
    image.srcset = srcset;
    image.setAttribute('data_max_resolution', buildUrl(3840));
    image.setAttribute('data-enhanced-public-id', publicId);
  });
}

/**
 * Wait for swapped slide and thumbnail images so cloned originals are not
 * revealed as four identical frames before `_02`–`_04` decode.
 *
 * @param {Element} root
 */
async function waitForEnhancedMainImage(root) {
  const images = [
    ...root.querySelectorAll('slideshow-slide img.product-media__image[data-enhanced-public-id]'),
    ...root.querySelectorAll('.slideshow-controls__thumbnail img[data-enhanced-public-id]'),
  ].filter((image) => image instanceof HTMLImageElement);

  if (!images.length) return;

  await withTimeout(
    Promise.all(images.map((image) => waitForImageReady(image).catch(() => {}))),
    1200
  ).catch(() => {});
}

/**
 * @param {HTMLImageElement} image
 */
async function waitForImageReady(image) {
  if (image.complete && image.naturalWidth > 0) {
    await image.decode?.().catch(() => {});
    return;
  }

  await new Promise((resolve, reject) => {
    image.addEventListener('load', resolve, { once: true });
    image.addEventListener('error', reject, { once: true });
  });

  await image.decode?.().catch(() => {});
}

/**
 * @param {HTMLImageElement} image
 */
function getImageWidths(image) {
  const srcsetWidths = Array.from(image.srcset.matchAll(/\s(\d+)w/g), (match) => Number(match[1])).filter(Boolean);

  if (srcsetWidths.length) return srcsetWidths;
  if (image.closest('.slideshow-controls__thumbnail')) return [144, 240, 352];
  if (image.closest('.dialog-thumbnails-list__thumbnail')) return [240, 352, 832, 1024];

  return DEFAULT_ENHANCED_IMAGE_WIDTHS;
}

/**
 * @param {Element} element
 * @param {string} attribute
 * @param {number} index
 */
function updateIndexedAction(element, attribute, index) {
  const value = element.getAttribute(attribute);

  if (!value) return;

  element.setAttribute(attribute, value.replace(/\/(?:open|select|handleThumbnailClick|handleThumbnailPointerEnter)\/\d+/, (match) =>
    match.replace(/\/\d+$/, `/${index}`)
  ));
}

/**
 * @template T
 * @param {Promise<T>} promise
 * @param {number} timeoutMs
 * @returns {Promise<T>}
 */
function withTimeout(promise, timeoutMs) {
  return new Promise((resolve, reject) => {
    const timeoutId = window.setTimeout(() => reject(new Error('Enhanced PDP assets request timed out')), timeoutMs);

    promise.then(resolve, reject).finally(() => window.clearTimeout(timeoutId));
  });
}

/**
 * @param {string} transformation
 */
function normalizeCloudinaryTransformation(transformation) {
  const normalizedTransformation = transformation.trim();

  if (!normalizedTransformation) return '';
  return normalizedTransformation.startsWith('/') ? normalizedTransformation : `/${normalizedTransformation}`;
}

/**
 * @param {string} value
 */
function stripFileExtension(value) {
  return value.replace(/\.[a-z0-9]+$/i, '');
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
